/** $18.500 — COP sin decimales, punto de miles, sin espacios raros de Intl */
export function formatearCOP(valor: number): string {
  const signo = valor < 0 ? '-' : '';
  const entero = Math.round(Math.abs(valor)).toLocaleString('es-CO', { maximumFractionDigits: 0 });
  return `${signo}$${entero}`;
}

/**
 * Zona del negocio. Todo día y toda hora del sistema se calculan aquí, no con el reloj del
 * dispositivo: un equipo con la zona mal configurada registraría ventas en otra fecha.
 */
const ZONA_NEGOCIO = 'America/Bogota';

/** Partes de un instante en la zona del negocio. `h23` evita el «24» de medianoche de `hour12`. */
function partesEnZona(instante: Date): Record<string, string> {
  const formato = new Intl.DateTimeFormat('en-US', {
    timeZone: ZONA_NEGOCIO,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const partes: Record<string, string> = {};
  for (const parte of formato.formatToParts(instante)) {
    if (parte.type !== 'literal') partes[parte.type] = parte.value;
  }
  return partes;
}

/** DD/MM/AAAA a partir de YYYY-MM-DD. Sin zona: la fecha ya viene resuelta. */
export function formatearFecha(fecha: string): string {
  const [y, m, d] = fecha.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

/** YYYY-MM-DD del día operativo en Colombia. */
export function fechaNegocio(instante: Date = new Date()): string {
  const p = partesEnZona(instante);
  return `${p.year}-${p.month}-${p.day}`;
}

/** HH:MM:SS en hora de Colombia. */
export function horaNegocio(instante: Date = new Date()): string {
  const p = partesEnZona(instante);
  return `${p.hour}:${p.minute}:${p.second}`;
}

/** DD/MM/AAAA HH:MM de un timestamptz, en hora de Colombia. */
export function fechaHoraNegocio(iso: string): string {
  const p = partesEnZona(new Date(iso));
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/** «agosto de 2026» en zona de Colombia, para fechas de alta. */
export function mesAnioNegocio(iso: string): string {
  return new Intl.DateTimeFormat('es-CO', {
    timeZone: ZONA_NEGOCIO,
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

/** Año y mes (1-12) del día operativo en Colombia, para los selectores de periodo. */
export function anioMesNegocio(instante: Date = new Date()): { anio: number; mes: number } {
  const p = partesEnZona(instante);
  return { anio: Number(p.year), mes: Number(p.month) };
}

/**
 * Los últimos cuatro dígitos de un documento. El dígito de verificación de un NIT viaja pegado al
 * número, así que el corte es por dígitos y no por caracteres.
 */
export function enmascararDocumento(numero: string | null): string {
  // El servidor ya puede mandarlo enmascarado: se respeta tal cual.
  if (numero?.startsWith('••••')) return numero;
  const digitos = (numero ?? '').replace(/\D/g, '');
  if (!digitos) return '—';
  return digitos.length <= 4 ? digitos : `••••${digitos.slice(-4)}`;
}
